const React = require('react');
const { useRef, useEffect, useState, useCallback } = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');

const { default: IconButton } = require('@mui/material/IconButton');
const { default: TextField } = require('@mui/material/TextField');
// const { default: SearchIcon } = require('@mui/icons-material/Search');
const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');
const { default: CancelIcon } = require('@mui/icons-material/Cancel');
const { default: ImportExportIcon } = require('@mui/icons-material/ImportExport');
const { default: ArrowRightAltIcon } = require('@mui/icons-material/ArrowRightAlt');
const { default: Backdrop } = require('@mui/material/Backdrop');
const { default: Badge } = require('@mui/material/Badge');
const { default: Tooltip } = require('@mui/material/Tooltip');

const AppPage = require('components/AppPage');
const LoadingScreen = require('components/LoadingScreen');
const RoutingTable = require('services/auth-management/routing-table');
const Continuation = require('services/auth-management/continuation');
const SchoolTile = require('components/SchoolTile');
const SimpleDialog = require('components/SimpleDialog');
const { getSchoolTileImageSrc } = require('utils/image');
const { context } = require('app-context');
const { useAppSelector } = require('utils/redux');
const { alphaSort } = require('utils/arrays');
const { transient$Props } = require('utils/styles');
const { useDispatch } = require('react-redux');
const { DIALOG_SCHOOL_IMG_HEIGHT } = require('utils/constants');

const {
    getDevice,
    isIOS: getIsIOS,
    isAndroid: getIsAndroid
} = require('utils/device');

const {
    actions,
    selectors: {
        all: selectors
    },
    api
} = context;

const LEFT_INDENT = 16;

const internals = {};

module.exports = function CommunityManagement({ location = window.location } = {}) {

    const currentSchool = useAppSelector(selectors.getCurrentSchool);
    const switchableCommunities = useAppSelector(selectors.getSwitchableCommunities);
    // const canJoinNearpeerCommunity = useAppSelector(selectors.getCanJoinNearpeerCommunity);

    const joinableCommunities = useAppSelector(selectors.getJoinableCommunities);

    const schools = useAppSelector(selectors.getSchools);
    const otherCommunityUnreadCounts = useAppSelector(selectors.getOtherCommunityUnreadCounts);

    // Filter out current school

    const [selectedSchool, setSelectedSchool] = useState(null);
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [leftCommunityIds, setLeftCommunityIds] = useState([]);

    const dispatch = useDispatch();

    const device = getDevice();
    const isIOS = getIsIOS(device);
    const isAndroid = getIsAndroid(device);
    const isMobile = isIOS || isAndroid;

    // const searchFilteredSchools = searchText === '' ? availableSchools : availableSchools.filter(({ name }) => name.toLowerCase().includes(searchText.toLowerCase()));

    // Filter out all communities and companies
    // const filteredSchools = searchFilteredSchools.filter(({ isCommunity, isCompany }) => {

    //     return !isCommunity && !isCompany;
    // });

    const {
        SchoolTilesListWrapper,
        SchoolTileRow,
        SchoolDetailsWrapper,
        ListItemButton,
        CommunityActionRail,
        SchoolMiddleInfo,
        SchoolName,
        StyledBackdrop,
        getSwitchPayload,
        toSchoolId
        // StyledTextField,
        // MaxHeightScrollWrapper,
        // LabelWrapper
    } = internals;

    const focusedSchoolId = toSchoolId(new URLSearchParams(location.search || '').get('focusCommunity'))
        || toSchoolId(Continuation.peek().pendingSchoolId);

    const joinCommunity = async (schoolId) => {

        const target = Continuation.getCommunityTarget(schoolId);
        setIsLoading(true);

        try {
            await dispatch(actions.profileManagement.joinSchool({
                schoolId
            }));

            await dispatch(actions.profileManagement.switchSchool({
                redirect: RoutingTable.REDIRECTS.joinSchoolProfile,
                schoolId,
                ...(target ? { target } : {})
            }));
        }
        finally {
            setIsLoading(false);
        }
    };

    const leaveCommunity = async (schoolId) => {

        setIsLoading(true);

        try {
            await dispatch(actions.profileManagement.leaveSchool({
                schoolId
            }));

            setLeftCommunityIds((ids) => ids.includes(schoolId) ? ids : ids.concat(schoolId));
        }
        finally {
            setIsLoading(false);
        }
    };

    const dialogConfig = {
        joinCommunity: {
            title: 'Join Community?',
            message: (school) => `Are you sure you want to join ${school.name}?`,
            action: () => joinCommunity(selectedSchool.id)
        },
        leaveCommunity: {
            title: 'Leave Community?',
            message: (school) => `Are you sure you want to leave ${school.name}?`,
            action: () => leaveCommunity(selectedSchool.id)
        },
        switchToCommunity: {
            title: 'Switch Community?',
            message: (school) => `Are you sure you want to switch from ${currentSchool?.name || 'current community'} to ${school?.name || 'selected community'}?`,
            action: async () => {

                setIsLoading(true);

                await dispatch(actions.profileManagement.switchSchool(getSwitchPayload(selectedSchool.id)));

                setIsLoading(false);
            }
        }
    };

    const serializedotherCommunityUnreadCounts = Object.entries(otherCommunityUnreadCounts).reduce((serialized, [schoolId, count]) => {

        return serialized + schoolId + count;
    }, '');

    const visibleSwitchableCommunities = switchableCommunities.filter(({ id }) => !leftCommunityIds.includes(id));

    const SchoolTilesList = useCallback((tilesListProps) => {

        const {
            list,
            dialogType: _dialogType,
            showType,
            showSubHeaders,
            showCloseButton,
            showJoinButton,
            showSwitchButton,
            centerRowHorizontal,
            showUnreadBadge = true,
            ...rest
        } = tilesListProps;

        const schoolTypes = {};

        list.forEach((school) => {

            let { type } = school;

            // Change type from 'root' to 'support'
            if (type === 'root') {
                type = 'support';
                school.type = type;
            }

            schoolTypes[type] = schoolTypes[type] || [];

            schoolTypes[type].push(school);
        });

        const sortedList = [];

        Object.keys(schoolTypes)
            .sort((a, b) => {

                if (a.toLowerCase() === 'support') {
                    return -1;
                }
                else if (b.toLowerCase() === 'support') {
                    return 1;
                }

                // Default alphabet sort the types
                return alphaSort()(a, b);
            })
            .forEach((type) => {

                sortedList.push(...schoolTypes[type]);
            });

        const renderedSubtitles = {};

        const newInviteIds = sortedList.filter(({ isNewInvite }) => isNewInvite).map(({ id }) => id);

        let hasRenderedNewInvite = false;

        return (
            <SchoolTilesListWrapper {...rest}>
                {sortedList.map((school, i) => {

                    const { type } = school;
                    const isCurrentSchool =  currentSchool.id === school.id;

                    const shouldRenderSubtitle = showSubHeaders && !renderedSubtitles[type];

                    renderedSubtitles[type] = true;

                    const { isNewInvite } = school;

                    let shouldScrollNewInvite = _dialogType === 'joinCommunity' && focusedSchoolId === Number(school.id);

                    if (!focusedSchoolId && _dialogType === 'joinCommunity' && isNewInvite && !hasRenderedNewInvite) {
                        hasRenderedNewInvite = true;
                        shouldScrollNewInvite = true;
                    }

                    return (
                        <React.Fragment key={school.id}>
                            {shouldRenderSubtitle && (
                                <Typography
                                    variant='h3'
                                    style={{
                                        marginTop: i === 0 ? 0 : 20,
                                        marginBottom: 10,
                                        fontSize: 20
                                    }}
                                >
                                    {type?.charAt(0)?.toUpperCase() + type?.slice(1)}{type !== 'support' && 's'}
                                </Typography>
                            )}
                            <SchoolTileRow
                                scrollIntoView={shouldScrollNewInvite}
                                onScrolledIntoView={async () => {

                                    // Mark all new schools as acknowledged
                                    if (newInviteIds.length) {
                                        await api.nearpeer.profileManagement.acknowledgeNewSchoolInvites({
                                            schoolIds: newInviteIds
                                        });
                                    }
                                }}
                            >
                                <SchoolDetailsWrapper>
                                    <ListItemButton
                                        onClick={() => {

                                            if (currentSchool.id === school.id) {
                                                return;
                                            }

                                            setSelectedSchool(school);
                                            setDialogType(_dialogType);
                                            setDialogIsOpen(true);
                                        }}
                                        $isCurrentSchool={isCurrentSchool}
                                        $centerContents={centerRowHorizontal}
                                    >
                                        <Badge
                                            badgeContent={isNewInvite ? 'NEW' : null}
                                            color='secondary'
                                            sx={{
                                                '& .MuiBadge-badge': {
                                                    fontSize: '0.6rem',
                                                    fontWeight: 'bold',
                                                    padding: '0 6px',
                                                    borderRadius: '4px'
                                                }
                                            }}
                                        >
                                            <SchoolTile
                                                schools={schools}
                                                isCurrentSchool={isCurrentSchool}
                                                slug={school.slug}
                                                showDetails
                                                unreadCount={showUnreadBadge ? otherCommunityUnreadCounts[school.id] : 0}
                                            />
                                        </Badge>
                                        <SchoolMiddleInfo>
                                            <SchoolName>
                                                {school.name}
                                                {showType && type !== 'support' && (
                                                    <Typography variant='body2'>
                                                        ({type})
                                                    </Typography>
                                                )}
                                            </SchoolName>
                                        </SchoolMiddleInfo>
                                    </ListItemButton>
                                    {((showSwitchButton && !isCurrentSchool) || (showCloseButton && school.canLeaveInstance) || showJoinButton) && (
                                        <CommunityActionRail
                                            onClick={(evt) => evt.stopPropagation()}
                                            onMouseDown={(evt) => evt.stopPropagation()}
                                        >
                                            {showSwitchButton && !isCurrentSchool && (
                                                <IconButton
                                            sx={{ color: '#000' }}
                                            style={{
                                                marginRight: (!showCloseButton || (showCloseButton && !school.canLeaveInstance)) ? 40 : 0
                                            }}
                                            aria-label={`Switch to ${school.name}`}
                                            data-focus-outline='radius:40,padding:3,zIndex:2'
                                            onClick={(evt) => {

                                                evt.stopPropagation();
                                                dispatch(actions.profileManagement.switchSchool(getSwitchPayload(school.id)));
                                            }}
                                        >
                                            <Tooltip
                                                arrow={true}
                                                title='Switch'
                                                placement='top'
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                                                            color: '#fff'
                                                        }
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: 'rgba(0, 0, 0, 0.9)'
                                                        }
                                                    }
                                                }}
                                            >
                                                <ImportExportIcon
                                                    style={{
                                                        transform: 'rotate(90deg)'
                                                    }}
                                                />
                                            </Tooltip>
                                                </IconButton>
                                            )}
                                            {showCloseButton && school.canLeaveInstance && (
                                                <IconButton
                                            sx={{ color: '#000' }}
                                            aria-label={`Leave ${school.name}`}
                                            data-focus-outline='radius:40,padding:3,zIndex:2'
                                            onClick={(evt) => {

                                                evt.stopPropagation();
                                                setSelectedSchool(school);
                                                setDialogType('leaveCommunity');
                                                setDialogIsOpen(true);
                                            }}
                                        >
                                            <Tooltip
                                                arrow={true}
                                                title='Leave'
                                                placement='top'
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                                                            color: '#fff'
                                                        }
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: 'rgba(0, 0, 0, 0.9)'
                                                        }
                                                    }
                                                }}
                                            >
                                                <CancelIcon />
                                            </Tooltip>
                                                </IconButton>
                                            )}
                                            {showJoinButton && (
                                                <Button
                                            size='small'
                                            color='secondary'
                                            variant='contained'
                                            style={{
                                                height: 31
                                            }}
                                            disableElevation
                                            data-focus-outline='radius:40,padding:3,zIndex:2'
                                            onClick={(evt) => {
                                                evt.stopPropagation();
                                                joinCommunity(school.id);
                                            }}
                                        >
                                            Join
                                        </Button>
                                            )}
                                        </CommunityActionRail>
                                    )}
                                </SchoolDetailsWrapper>
                            </SchoolTileRow>
                        </React.Fragment>
                    );
                })}
            </SchoolTilesListWrapper>
        );
    }, [currentSchool?.id, schools, serializedotherCommunityUnreadCounts, focusedSchoolId]);

    SchoolTilesList.propTypes = {
        list: T.arrayOf(T.shape({
            id: T.number.isRequired,
            name: T.string.isRequired,
            slug: T.string.isRequired
        })),
        dialogType: T.string,
        showCloseButton: T.bool
    };

    const selectedDialogConfig = dialogConfig[dialogType];

    const selectedSchoolSvgSrc = getSchoolTileImageSrc(selectedSchool);

    const currentSchoolSvgSrc = getSchoolTileImageSrc(currentSchool);

    const onConfirm = async () => {

        setDialogIsOpen(false);

        await selectedDialogConfig?.action?.();
    };

    return (
        <AppPage>
            <h2 style={{ width: '100%' }}>
                Your Current Community
            </h2>

            <SchoolTilesList
                list={[currentSchool]}
                style={{ paddingTop: 0 }}
                centerRowHorizontal
                showUnreadBadge={false}
            />

            <Typography style={{ width: '100%', marginBottom: 0, marginTop: 14 }}>
                Click below to switch to a different community{!!joinableCommunities.length && ' -- or to join a new one'}
            </Typography>

            {!!visibleSwitchableCommunities.length && (<>
                <h2 style={{ width: '100%', marginTop: 30, marginBottom: 30 }}>
                    Your Other Communities
                </h2>
                <SchoolTilesList
                    list={visibleSwitchableCommunities}
                    dialogType='switchToCommunity'
                    showSubHeaders
                    showCloseButton
                    showSwitchButton
                />
            </>)}

            {(!!joinableCommunities.length) && (<>
                <h2 style={{ width: '100%', marginTop: 24 }}>
                    Communities You Can Join
                </h2>

                <SchoolTilesList
                    list={joinableCommunities}
                    dialogType='joinCommunity'
                    showSubHeaders
                    showJoinButton
                />
            </>)}

            {/* Community search list */}

            {/* <h2 style={{ marginTop: 30 }}>
                Find a Community
            </h2>

            <StyledTextField
                id='search'
                fullWidth
                variant='outlined'
                placeholder={'Type to search...'}
                label={(
                    <LabelWrapper>
                        <SearchIcon sx={{ marginRight: 1 }} />
                        <Typography>
                            Search
                        </Typography>
                    </LabelWrapper>
                )}
                value={searchText}
                onChange={(evt) => setSearchText(evt.target.value)}
            />

            <MaxHeightScrollWrapper>
                <SchoolTilesListWrapper>
                    {filteredSchools.map((school, i) => {

                        const isLastItem = i === filteredSchools.length - 1;

                        return (
                            <SchoolTileRow key={school.id}>
                                <SchoolDetailsWrapper>
                                    <ListItemButton onClick={() => {

                                        setSelectedSchool(school);
                                        setDialogType('joinCommunity');
                                        setDialogIsOpen(true);
                                    }}>
                                        <SchoolTile
                                            schools={schools}
                                            isCurrentSchool={currentSchool.id === school.id}
                                            slug={school.slug}
                                            showDetails
                                        />
                                        <SchoolMiddleInfo>
                                            <SchoolName>{school.name}</SchoolName>
                                        </SchoolMiddleInfo>
                                    </ListItemButton>
                                </SchoolDetailsWrapper>
                                {!isLastItem && <Divider />}
                            </SchoolTileRow>
                        );
                    })}
                </SchoolTilesListWrapper>
            </MaxHeightScrollWrapper> */}

            <SimpleDialog
                isOpen={dialogIsOpen && selectedDialogConfig}
                onConfirm={onConfirm}
                onRequestClose={() => setDialogIsOpen(false)}
                title={selectedDialogConfig?.title}
                content={(
                    <div
                        style={{
                            display: 'flex',
                            flexFlow: 'column nowrap',
                            alignItems: 'center',
                            width: isMobile ? 'auto' : '421px'
                        }}>
                        {dialogType === 'leaveCommunity' ? (
                            <img
                                src={selectedSchoolSvgSrc}
                                style={{ height: DIALOG_SCHOOL_IMG_HEIGHT, maxWidth: 140 }}
                                alt={selectedSchool?.name || 'Selected community'}
                            />
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row nowrap',
                                    alignItems: 'center',
                                    gap: 16,
                                    marginBottom: 20
                                }}>
                                <img
                                    src={currentSchoolSvgSrc}
                                    style={{ height: DIALOG_SCHOOL_IMG_HEIGHT, maxWidth: 100 }}
                                    alt={currentSchool?.name || 'Current community'}
                                />
                                <ArrowRightAltIcon
                                    style={{
                                        fontSize: 52
                                    }}
                                />
                                <img
                                    src={selectedSchoolSvgSrc}
                                    style={{ height: DIALOG_SCHOOL_IMG_HEIGHT, maxWidth: 100 }}
                                    alt={selectedSchool?.name || 'Selected community'}
                                />
                            </div>
                        )}
                        <Typography
                            style={{
                                marginTop: 20
                            }}
                        >
                            {selectedDialogConfig?.message(selectedSchool)}
                        </Typography>
                    </div>
                )}
                buttons={(
                    <div style={{ display: 'flex', flexFlow: 'row nowrap', gap: 10 }}>
                        <Button
                            onClick={() => setDialogIsOpen(false)}
                            color='secondary'
                            variant='contained'
                            data-focus-outline='radius:40'
                        >
                            No
                        </Button>
                        <Button
                            onClick={onConfirm}
                            color='primary'
                            variant='contained'
                            data-focus-outline='radius:40'
                            autoFocus
                        >
                            Yes
                        </Button>
                    </div>
                )}
            />
            <StyledBackdrop open={isLoading}>
                <LoadingScreen variant='content' />
            </StyledBackdrop>
        </AppPage>
    );
};

internals.toSchoolId = (value) => {

    const schoolId = Number(value);
    return /^\d+$/.test(String(value)) && Number.isSafeInteger(schoolId) && schoolId > 0 ? schoolId : undefined;
};

internals.getSwitchPayload = (schoolId) => {

    const target = Continuation.getCommunityTarget(schoolId);
    const held = Continuation.peek();
    const needsProfile = held.redirect === RoutingTable.REDIRECTS.joinSchoolProfile
        || Number(held.pendingProfileSchoolId) === Number(schoolId);
    const redirect = needsProfile ? RoutingTable.REDIRECTS.joinSchoolProfile : RoutingTable.REDIRECTS.afterAuth;
    return { schoolId, ...(target ? { redirect, target } : {}) };
};

internals.SchoolTilesListWrapper = Styled.div`
    padding-top: ${({ theme }) => theme.spacing(2)};
    display: flex;
    flex-flow: column nowrap;
    width: 100%;
    max-width: 560px;
    margin: 0 auto;
    box-sizing: border-box;

    @media screen and (max-width: 600px) {
        padding-right: 20px;
        padding-left: 20px;
    }
`;


internals.SchoolTileRow = function SchoolTileRow(props) {

    const {
        scrollIntoView = false,
        onScrolledIntoView,
        ...rest
    } = props;

    const ref = useRef(null);

    useEffect(() => {

        let scrollTimer;
        let acknowledgementTimer;
        if (scrollIntoView && ref.current) {
            scrollTimer = setTimeout(() => {

                if (!ref.current) {
                    return;
                }

                ref.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                acknowledgementTimer = setTimeout(onScrolledIntoView, 1500);
            }, 500);
        }

        return () => {

            clearTimeout(scrollTimer);
            clearTimeout(acknowledgementTimer);
        };
    }, [scrollIntoView]);

    return (
        <div
            ref={ref}
            style={{ width: '100%' }}
            {...rest}
        />
    );
};

internals.SchoolTileRow.propTypes = {
    scrollIntoView: T.bool,
    onScrolledIntoView: T.func
};

internals.SchoolDetailsWrapper = Styled.div`
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    align-items: stretch;
    box-sizing: border-box;
    margin: 4px 0 14px;
    padding: 10px 12px 10px 0;
    border-radius: 16px;
    background: rgba(248, 250, 252, 0.96);
    border: 1px solid rgba(18, 39, 79, 0.09);
    overflow: hidden;
    transition: background 120ms ease, border-color 120ms ease;

    &:hover {
        background: ${({ theme }) => theme.palette.primary.main}0F;
        border-color: ${({ theme }) => theme.palette.primary.main}33;
    }
`;


internals.ListItemButton = Styled('div', transient$Props)`
    padding: ${({ theme }) => theme.spacing(2)} 0;
    padding-left: ${({ $centerContents }) => $centerContents ? 0 : LEFT_INDENT}px;
    justify-content: ${({ $centerContents }) => $centerContents ? 'center' : 'flex-start'};
    cursor: ${({ $isCurrentSchool }) => $isCurrentSchool ? 'default' : 'pointer'};
    flex: 1;
    min-width: 0;
    min-height: 66px;

    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    background: transparent !important;

    @media (max-width: 600px) {
        padding-left: ${({ $centerContents }) => $centerContents ? 0 : LEFT_INDENT / 2}px;
    }
`;

internals.CommunityActionRail = Styled.div`
    width: auto;
    min-width: 68px;
    flex: 0 0 auto;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    gap: 6px;
    margin-left: 8px;
    padding-left: 12px;
    border-left: 1px solid rgba(18, 39, 79, 0.10);
    background: transparent;
    cursor: default;

    .MuiIconButton-root,
    .MuiButtonBase-root {
        box-shadow: none !important;
    }

    .MuiIconButton-root:hover,
    .MuiButtonBase-root:hover,
    .Mui-focusVisible {
        box-shadow: none !important;
    }
`;

internals.SchoolMiddleInfo = Styled.div`
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    min-width: 0;
`;


internals.SchoolName = Styled.h3`
    margin: 0;
    margin-left: ${({ theme }) => theme.spacing(3)};
    font-size: 18px;
    line-height: 1.25;
`;


internals.StyledTextField = Styled(TextField)`
    && {
        &.MuiInputBase-root {
            height: 100%;
        }

        .MuiInput-input {
            text-align: center;
        }
    }

    width: 100%;

    label p {
        font-size: 18px;
    }
`;

internals.MaxHeightScrollWrapper = Styled.div`
    max-height: 500px;
    overflow: auto;
    margin-top: ${({ theme }) => theme.spacing(2)};
`;

internals.LabelWrapper = Styled.div`
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-contents: space-between;
`;

internals.StyledBackdrop = Styled(Backdrop)`
  && {
    z-index: 2000;
    background-color: rgba(0, 0, 0, 0.2);
  }
`;
